import { createSlice } from "@reduxjs/toolkit";
import toast from "react-hot-toast";

// Helper function to extract normalized numeric price from cart item
const getNumericPrice = (item) => {
  if (!item) return 0;
  const price = item.selectedProductPrice ?? item.price ?? item.finalPrice ?? item.unitPrice ?? 0;
  const num = Number(price);
  return isNaN(num) ? 0 : num;
};

// Helper function to extract size identifier string
const getSizeKey = (selectedSize) => {
  if (!selectedSize) return "standard";
  if (typeof selectedSize === "object" && selectedSize.packageSize) {
    return String(selectedSize.packageSize).trim().toLowerCase();
  }
  return String(selectedSize).trim().toLowerCase();
};

// Helper function to calculate total amount and total quantity accurately
const calculateTotals = (cartItems = []) => {
  return cartItems.reduce(
    (acc, item) => {
      const price = getNumericPrice(item);
      const qty = Number(item.cartQuantity) || 1;
      acc.totalAmount += price * qty;
      acc.totalQuantity += qty;
      return acc;
    },
    { totalAmount: 0, totalQuantity: 0 }
  );
};

// Helper function to safely load and normalize cart items from localStorage
const loadCartFromStorage = () => {
  try {
    const rawCart = localStorage.getItem("cart");
    if (!rawCart) return [];
    const parsed = JSON.parse(rawCart);
    if (!Array.isArray(parsed)) return [];

    return parsed.map((item) => {
      const numericPrice = getNumericPrice(item);
      const sizeKey = getSizeKey(item.selectedSize);
      const cartItemId = item.cartItemId || `${item.id}_${sizeKey}`;
      return {
        ...item,
        cartItemId,
        selectedProductPrice: numericPrice,
        price: numericPrice,
        cartQuantity: Math.max(1, Number(item.cartQuantity) || 1),
      };
    });
  } catch (err) {
    console.error("Error reading cart from localStorage:", err);
    return [];
  }
};

const initialCartItems = loadCartFromStorage();
const initialTotals = calculateTotals(initialCartItems);

const initialState = {
  cartState: false,
  cartItems: initialCartItems,
  cartTotalAmount: initialTotals.totalAmount,
  cartTotalQantity: initialTotals.totalQuantity,
};

const CartSlice = createSlice({
  initialState,
  name: "cart",
  reducers: {
    setOpenCart: (state, action) => {
      state.cartState = action.payload?.cartState ?? true;
    },
    setCloseCart: (state, action) => {
      state.cartState = action.payload?.cartState ?? false;
    },

    setAddItemToCart: (state, action) => {
      const payload = action.payload;
      if (!payload) return;

      const { id, name } = payload;
      const numericPrice = getNumericPrice(payload);
      const sizeKey = getSizeKey(payload.selectedSize);
      const cartItemId = payload.cartItemId || `${id}_${sizeKey}`;

      const existingIndex = state.cartItems.findIndex(
        (item) => item.cartItemId === cartItemId || (item.id === id && getSizeKey(item.selectedSize) === sizeKey)
      );

      if (existingIndex >= 0) {
        state.cartItems[existingIndex].cartQuantity += 1;
        state.cartItems[existingIndex].selectedProductPrice = numericPrice;
        state.cartItems[existingIndex].price = numericPrice;
        toast.success(`Updated ${name || "Item"} quantity in Cart`);
      } else {
        const newItem = {
          ...payload,
          cartItemId,
          selectedProductPrice: numericPrice,
          price: numericPrice,
          cartQuantity: 1,
        };
        state.cartItems.push(newItem);
        toast.success(`${name || "Item"} added to Cart`);
      }

      const { totalAmount, totalQuantity } = calculateTotals(state.cartItems);
      state.cartTotalAmount = totalAmount;
      state.cartTotalQantity = totalQuantity;

      localStorage.setItem("cart", JSON.stringify(state.cartItems));
      localStorage.setItem("totalAmount", JSON.stringify(state.cartTotalAmount));
    },

    setRemoveItemFromCart: (state, action) => {
      const target = action.payload;
      const targetId = typeof target === "object" && target !== null ? (target.cartItemId || target.id) : target;
      const targetSizeKey = typeof target === "object" && target !== null ? getSizeKey(target.selectedSize) : null;

      const itemsAfterRemoval = state.cartItems.filter((item) => {
        if (item.cartItemId && item.cartItemId === targetId) return false;
        if (targetSizeKey && item.id === targetId && getSizeKey(item.selectedSize) === targetSizeKey) return false;
        return item.id !== targetId;
      });

      state.cartItems = itemsAfterRemoval;

      const { totalAmount, totalQuantity } = calculateTotals(state.cartItems);
      state.cartTotalAmount = totalAmount;
      state.cartTotalQantity = totalQuantity;

      localStorage.setItem("cart", JSON.stringify(state.cartItems));
      localStorage.setItem("totalAmount", JSON.stringify(state.cartTotalAmount));

      const itemName = typeof target === "object" && target?.name ? target.name : "Item";
      toast.success(`${itemName} Removed From Cart`);
    },

    setIncreaseItemQTY: (state, action) => {
      const target = action.payload;
      const targetId = typeof target === "object" && target !== null ? (target.cartItemId || target.id) : target;
      const targetSizeKey = typeof target === "object" && target !== null ? getSizeKey(target.selectedSize) : null;

      const itemIndex = state.cartItems.findIndex((item) => {
        if (item.cartItemId && item.cartItemId === targetId) return true;
        if (targetSizeKey && item.id === targetId && getSizeKey(item.selectedSize) === targetSizeKey) return true;
        return item.id === targetId;
      });

      if (itemIndex >= 0) {
        state.cartItems[itemIndex].cartQuantity += 1;
        toast.success("Item QTY Increased");
      }

      const { totalAmount, totalQuantity } = calculateTotals(state.cartItems);
      state.cartTotalAmount = totalAmount;
      state.cartTotalQantity = totalQuantity;

      localStorage.setItem("cart", JSON.stringify(state.cartItems));
      localStorage.setItem("totalAmount", JSON.stringify(state.cartTotalAmount));
    },

    setDecreaseItemQTY: (state, action) => {
      const target = action.payload;
      const targetId = typeof target === "object" && target !== null ? (target.cartItemId || target.id) : target;
      const targetSizeKey = typeof target === "object" && target !== null ? getSizeKey(target.selectedSize) : null;

      const itemIndex = state.cartItems.findIndex((item) => {
        if (item.cartItemId && item.cartItemId === targetId) return true;
        if (targetSizeKey && item.id === targetId && getSizeKey(item.selectedSize) === targetSizeKey) return true;
        return item.id === targetId;
      });

      if (itemIndex >= 0) {
        if (state.cartItems[itemIndex].cartQuantity > 1) {
          state.cartItems[itemIndex].cartQuantity -= 1;
          toast.success("Item QTY Decreased");
        }
      }

      const { totalAmount, totalQuantity } = calculateTotals(state.cartItems);
      state.cartTotalAmount = totalAmount;
      state.cartTotalQantity = totalQuantity;

      localStorage.setItem("cart", JSON.stringify(state.cartItems));
      localStorage.setItem("totalAmount", JSON.stringify(state.cartTotalAmount));
    },

    setClearCartItems: (state) => {
      state.cartItems = [];
      state.cartTotalAmount = 0;
      state.cartTotalQantity = 0;

      localStorage.setItem("cart", JSON.stringify([]));
      localStorage.setItem("totalAmount", JSON.stringify(0));
      toast.success("Cart Cleared");
    },

    setGetTotals: (state) => {
      const { totalAmount, totalQuantity } = calculateTotals(state.cartItems);
      state.cartTotalAmount = totalAmount;
      state.cartTotalQantity = totalQuantity;
      localStorage.setItem("totalAmount", JSON.stringify(totalAmount));
    },
  },
});

export const {
  setOpenCart,
  setCloseCart,
  setAddItemToCart,
  setRemoveItemFromCart,
  setIncreaseItemQTY,
  setDecreaseItemQTY,
  setClearCartItems,
  setGetTotals,
} = CartSlice.actions;

export const selectCartState = (state) => state.cart.cartState;
export const selectCartItems = (state) => state.cart.cartItems;
export const selectTotalAmount = (state) => state.cart.cartTotalAmount;
export const selectTotalQTY = (state) => state.cart.cartTotalQantity;

export default CartSlice.reducer;